import { useState } from 'react';
import { Navigate } from 'react-router';
import { signInWithGoogle } from '@/lib/supabase/auth';
import { env } from '@/env';
import { useSession } from '@/features/auth/useSession';
import { FullScreenSpinner } from '@/app/layouts/FullScreenSpinner';
import { AuthLayout } from './AuthLayout';

export function LoginScreen() {
  const { session, loading } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (loading) return <FullScreenSpinner label="Checking your session" />;
  if (session) return <Navigate to="/board" replace />;

  async function start() {
    setBusy(true);
    setError(null);
    try {
      await signInWithGoogle();
    } catch (err) {
      setBusy(false);
      setError(err instanceof Error ? err.message : 'Could not start sign-in.');
    }
  }

  return (
    <AuthLayout>
      <img
        src="/logo.svg"
        alt=""
        width={44}
        height={44}
        className="mx-auto mb-4 dark-invert"
        style={{ width: 44, height: 44 }}
      />
      <h1 className="text-lg font-semibold tracking-tight">Tap Academy</h1>
      <p className="mt-2 text-sm" style={{ color: 'var(--text-muted)' }}>
        Your tasks, and the desks that assign them.
      </p>

      {/*
        The only way in. No password field, no "or" divider, no second path — the
        account you use at work is the account you use here.
      */}
      <button
        type="button"
        onClick={() => void start()}
        disabled={busy}
        className="mt-7 flex min-h-11 w-full items-center justify-center gap-2.5 rounded-lg border px-4 text-sm font-medium transition-colors disabled:opacity-60"
        style={{ borderColor: 'var(--border)', background: 'var(--surface-raised)' }}
      >
        <GoogleMark />
        {busy ? 'Opening Google…' : 'Continue with Google'}
      </button>

      {error && (
        <p className="mt-4 text-xs" style={{ color: 'var(--color-priority-urgent)' }}>
          {error}
        </p>
      )}

      <p className="mt-6 text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
        {env.allowedEmailDomain === '*'
          ? 'Any Google account, for testing. There is no password to remember.'
          : `Company ${env.allowedEmailDomain} accounts only. There is no password to remember.`}
      </p>
    </AuthLayout>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" aria-hidden>
      <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 0 1-2.4 3.7v3h3.9c2.3-2.1 3.5-5.2 3.5-8.9z" />
      <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3c-1.1.7-2.4 1.2-4 1.2-3.1 0-5.7-2.1-6.6-4.9H1.4v3.1A12 12 0 0 0 12 24z" />
      <path fill="#FBBC05" d="M5.4 14.3a7.2 7.2 0 0 1 0-4.6V6.6H1.4a12 12 0 0 0 0 10.8l4-3.1z" />
      <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.4 6.6l4 3.1C6.3 6.9 8.9 4.8 12 4.8z" />
    </svg>
  );
}
